import React, { Component } from 'react'
import { injectIntl } from 'react-intl'
import cx from 'classnames'

import Routes from 'src/app/routes'
import {
  hasLocalStorage,
  getCurrentPageUrlInfo,
  getParamFromUrlWithoutRouter,
  getPageClassFromUrl,
  getKioskDocument,
  getChatChannelName,
  getOrCreateChatIdentity,
} from 'src/utils/helpers'
import PushService from 'src/utils/push-service'
import EventsService from 'src/utils/events-service'
import { Sidebar, Content, Container, Layout } from 'src/components/Layout'
import HeaderMain from 'src/components/Layout/Header/HeaderMain'
import OfflineStatus from 'src/modules/OfflineStatus'
import AddToHomeScreen from 'src/modals/AddToHomeScreen'
import NetworkProvider from 'src/components/NetworkProvider'
import IdleDetect from 'src/components/Events/IdleDetect'
import IdleLogOut from 'src/components/Events/IdleLogOut'
import NotificationBanner from 'src/components/NotificationBanner'
import intlMessagesApp from 'src/modules/App/translations'
import intlMessages from './translations'
import withToast from 'src/components/Toast'

import styles from './AppLayout.module.less'
import chatService from 'src/utils/ChatService'
import { Loader } from 'src/components/Loader'

class AppLayout extends Component {
  state = {
    chatInitiated: false,
  }

  componentDidMount() {
    const {
      setIsWidget,
      featureFlags,
      isWidget,
      user,
      location,
      setDisclaimer,
      chat,
      resetApp,
    } = this.props
    setDisclaimer(true)
    setIsWidget(isWidget)
    if (PushService.isEnabled('chat', user, featureFlags, isWidget)) {
      // Just sync existing subscription
      PushService.updatePushSubscription(true, isWidget)
    }

    if (!isWidget) {
      const loginFromLinkReferrer = getParamFromUrlWithoutRouter(location, 4, null)

      // Wait until everything is settled and someone is actually visiting the page
      // Useful for waiting to see current access type
      setTimeout(() => {
        const { userLastAccessType } = this.props

        // Take "loginFromLinkReferrer" if set because we can find that from URL right away,
        // where "userLastAccessType" takes time to be set from EmailVerification.componentDidMount
        const accessType = loginFromLinkReferrer || userLastAccessType

        const visitEventType = accessType === 'logInAsKioskUserLink' ? 'visitAsKioskUser' : 'visit'

        EventsService.registerEvent({ isWidget }, 'access', visitEventType)
      }, 3000)
    }

    const currentPageClass = getPageClassFromUrl(location.pathname)
    this.addClassBody(currentPageClass)

    if (isWidget) {
      const doc = getKioskDocument()
      const docBody = doc.querySelector('body')
      this.addClassBody(currentPageClass, docBody)
    }
    if (chat.isEnabled) {
      if (this.props.user && window.location.href.includes('logInAsKioskUserLink')) {
        resetApp('logInAsKioskUserLink', false, null, true)
        this.initChat()
      } else {
        this.initChat()
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { location, isWidget } = this.props

    if (location.pathname !== prevProps.location.pathname) {
      const prevPageClass = getPageClassFromUrl(prevProps.location.pathname)
      this.removeClassBody(prevPageClass)

      const currentPageClass = getPageClassFromUrl(location.pathname)
      this.addClassBody(currentPageClass)

      if (isWidget) {
        const doc = getKioskDocument()
        const docBody = doc.querySelector('body')
        this.removeClassBody(prevPageClass, docBody)
        this.addClassBody(currentPageClass, docBody)
      }
    }
    if (!prevProps.chatReset && this.props.chatReset) {
      chatService.shutdown()
      // resetting chat class properties
      chatService.reset()
      // if user logged out initiate a new chat service
      if (!this.props.user && this.props.chat.isEnabled) {
        this.initChat()
      }
    }
  }

  async initChat() {
    this.props.setAppLoading(true)
    const { intl, history } = this.props
    const channelName = getChatChannelName()
    const identity = getOrCreateChatIdentity(this.props.user)
    try {
      // Load or create a chat
      await chatService.initialize(intl, history, channelName, identity)
    } catch (e) {
      if (e) {
        const message =
          (e.error && e.error.body && e.error.body.message) || e.message || e.toString()
        // Show error if chat couldn't be configured
        // skip error message if conversion is expired or user doesn't have one
        if (message && !message.toLowerCase().includes('not found')) {
          console.error(message)
        }
      }
    }
    chatService.addChannelEventListeners()
    this.props.setAppLoading(false)
    this.props.setChatInit()
  }

  addClassBody(cls, elem = document.body) {
    elem.classList.add(cls)
  }

  removeClassBody(cls, elem = document.body) {
    elem.classList.remove(cls)
  }

  render() {
    if (this.props.isAppLoading) return <Loader />
    const {
      setRenderedAppState,
      currentlyOpenFeature,
      isSidebarVisible,
      isWidget,
      isWidgetActive,
      isWidgetFullSize,
      location,
      intl,
      bannerNotification,
      toast,
    } = this.props

    if (!hasLocalStorage()) {
      toast({
        title: intl.formatMessage(intlMessagesApp.appErrorTitle),
        description: intl.formatMessage(intlMessages.appCookiesRequiredMessage),
        status: 'error',
      })

      return null
    }
    const currentPageCls = getCurrentPageUrlInfo(location, 'cls')
    const currentPageUrl = getCurrentPageUrlInfo(location, 'url')

    // TODO: Converted to modular CSS

    const classNamesLayout =
      'kiosk-first-layout kiosk--chakra-colors-background ' +
      currentPageCls +
      (currentlyOpenFeature ? ' ' + currentlyOpenFeature : '') +
      ' is-widget-' +
      (isWidget ? 'yes' : 'no')

    const classNamesAppMainLayout = cx({
      [styles.appMainLayout]: true,
      [styles.appMainLayoutOpenSidebarOnMobile]: isSidebarVisible === true,
      // 'app-main-layout': true,
      'kiosk-chakra-colors-background': true,
    })
    const classNamesAppMainContainerInner = cx({
      [styles.mainContainerInner]: true,
      [styles.mainContainerInnerHeightFull]: currentPageCls && currentPageCls.includes('chat'),
      [styles.mainContainerInnerHeightAuto]:
        currentPageCls && currentPageCls.includes('requestQuote'),
      'app-main-container-inner': true,
      'kiosk-widget--layout-padding-for-nav-scroll':
        isWidget && !currentPageCls.includes('profile') && !currentPageCls.includes('chat'),
      'full-height': currentPageCls.includes('welcome'),
      'kiosk--layout-padding-for-nav-scroll':
        !(currentPageCls && currentPageCls.includes('chat')) &&
        !currentPageUrl.includes('documents/item'),
    })

    const classnamesContent = cx({
      'app-main-content': true,
      'app-main-content__widget': isWidget,
      'app-main-content__chat': currentPageCls && currentPageCls.includes('chat'),
    })

    return (
      <Layout className={classNamesLayout}>
        {!isWidget ? (
          <React.Fragment>
            <NetworkProvider />
            <AddToHomeScreen />
            <OfflineStatus />
          </React.Fragment>
        ) : (
          ''
        )}
        <Layout className={classNamesAppMainLayout} flexDirection='row'>
          <IdleDetect />
          <IdleLogOut />
          <HeaderMain
            isWidget={!!isWidget}
            isWidgetActive={!!isWidgetActive}
            isWidgetFullSize={!!isWidgetFullSize}
            isHome={currentPageCls && currentPageCls.includes('welcome')}
          />
          <Sidebar />
          <Content className={classnamesContent}>
            <Container className='app-main-container'>
              <NotificationBanner bannerNotification={bannerNotification} />
              <div className={classNamesAppMainContainerInner}>
                <Routes rendered={setRenderedAppState} />
              </div>
            </Container>
          </Content>

          {/* <NavBottom className={styles.mainNavBottom} /> */}
        </Layout>
      </Layout>
    )
  }
}

export default injectIntl(withToast(AppLayout))
